import type { HeroId } from '../../../shared/game/world-types.js';
export type SelectionMode = 'replace' | 'add' | 'toggle';
/**
 * `ids` in the party column order a player saved: heroes are known by `keyOf` (their saved character, which outlives a room's seat ids),
 * those in `saved` come first in its order, and the rest follow as they were.
 */
export function inSavedOrder(ids: readonly HeroId[], keyOf: (id: HeroId) => string, saved: readonly string[]): HeroId[] {
  const rank = (id: HeroId) => { const at = saved.indexOf(keyOf(id)); return at < 0 ? saved.length : at; };
  return ids.map((id, index) => ({ id, index })).sort((a, b) => rank(a.id) - rank(b.id) || a.index - b.index).map(entry => entry.id);
}
/** The column order to save after a reorder: this party's keys in their new order, then the other characters the player saved before. */
export function savedOrderAfter(owned: readonly HeroId[], keyOf: (id: HeroId) => string, saved: readonly string[]): string[] {
  const keys = owned.map(keyOf);
  return [...keys, ...saved.filter(key => !keys.includes(key))];
}
/** Local command scope; the server independently checks ownership. */
export class HeroSelection {
  owned: HeroId[] = [];
  ids: HeroId[] = [];
  reset(owned: HeroId[]) { this.owned = [...owned]; this.ids = [...owned]; }
  /** Moves one of this player's heroes to place `index` of their party column (top first); the same order fills formation slots and the number keys. */
  move(id: HeroId, index: number) {
    if (!this.owned.includes(id)) return;
    const rest = this.owned.filter(other => other !== id);
    rest.splice(Math.max(0, Math.min(rest.length, index)), 0, id);
    this.owned = rest; this.ids = this.owned.filter(other => this.ids.includes(other));
  }
  select(ids: HeroId[], mode: SelectionMode = 'replace') {
    const next = new Set(mode === 'replace' ? [] : this.ids);
    for (const id of ids) if (this.owned.includes(id)) {
      if (mode === 'toggle' && next.has(id)) next.delete(id); else next.add(id);
    }
    this.ids = this.owned.filter(id => next.has(id));
  }
}
